import { useState, useEffect, useCallback } from "react";
import { X, Download } from "lucide-react";

const DISMISS_KEY = "charstan_pwa_prompt_dismissed";
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  const handleBeforeInstall = useCallback((e) => {
    e.preventDefault();
    // Check if user previously dismissed
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_DURATION) {
      return;
    }
    setDeferredPrompt(e);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, [handleBeforeInstall]);

  // Hide if already installed
  useEffect(() => {
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
    setInstalling(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-[9998] animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="bg-[#111827] border border-[#1E3A5F]/60 rounded-xl shadow-2xl p-4 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-[#C9A84C]/20 to-[#C9A84C]/5 flex items-center justify-center border border-[#C9A84C]/20">
            <Download className="w-5 h-5 text-[#C9A84C]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#F1F5F9] mb-0.5">
              Install CHARSTAN
            </h3>
            <p className="text-xs text-[#94A3B8] leading-relaxed">
              Add to your home screen for quick access across all your devices.
            </p>
          </div>
          <button
            data-testid="pwa-install-dismiss"
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded-md text-[#64748B] hover:text-[#94A3B8] hover:bg-[#1E293B] transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3 ml-[52px]">
          <button
            data-testid="pwa-install-button"
            onClick={handleInstall}
            disabled={installing}
            className="px-4 py-1.5 text-xs font-medium rounded-lg bg-[#C9A84C] text-[#0B0E14] hover:bg-[#D4B85C] transition-colors disabled:opacity-50"
          >
            {installing ? "Installing..." : "Install"}
          </button>
          <button
            data-testid="pwa-install-not-now"
            onClick={handleDismiss}
            className="px-4 py-1.5 text-xs font-medium rounded-lg text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#1E293B] transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
